<template>
  <div
    class="user-permissions"
  >
    <div class="popup">
      <div class="head">
        <span class="name">{{ $t('team_popup.user_permissions_title', {name: userInfo.name}) }}</span>
        <UiIcon
          :size="20"
          class="close"
          name="times"
          @click="close"
        />
      </div>

      <div
        ref="scroll"
        v-loading="isLoading"
        class="permissions-wrapper"
      >
        <div class="select-block select-block__full">
          <span class="label">{{ $t('permissions.teams_select') }}</span>
          <UiSelectElement
            ref="selectClose"
            v-model="currentTeamSelect"
            :placeholder="$t('permissions.teams_select')"
            :list="userTeams"
            filterable
            size="big"
          />
        </div>
        <ElCollapse v-model="activePermissions">
          <ElCollapseItem
            :title="$t('permissions.reports.title')"
            name="reports"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.reports.reports_card_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view')" />

                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.reports.reports_affiliates_types') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('reports_affiliates_types')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>
                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.reports_affiliates_types"
                              :disabled="inheritIsDisabled('reports_affiliates_types')"
                              @change="refreshInheritReports(inherits.reports_affiliates_types)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.reports_affiliates_types"
                      :disabled="reportsDisabledColumns"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="adminPermissions.reports_view_affiliates_visibility_teammates_id"
                        :disabled="reportsDisabledColumns"
                        :users-list="users"
                        :affiliates-types="adminPermissions.reports_affiliates_types"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.reports.reports_group_by') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_group_by_affiliates')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_group_by_countries')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_group_by_sub_affiliates')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_group_by_payments')" />
                </div>
              </div>
            </div>
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.reports.reports_view_columns') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_revenue_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_profit_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_deposits_sum_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_deposits_count_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_withdrawals_sum_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_withdrawals_count_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_sport_bets_sum_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_sport_bets_count_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_casino_bets_sum_columns')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('reports_view_casino_bets_count_columns')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="$t('permissions.affiliates.title')"
            name="affiliates"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_card_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_view')" />
                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.affiliates.affiliates_types_view') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('affiliates_types_view')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.affiliates_types_view"
                              :disabled="inheritIsDisabled('affiliates_types_view')"
                              @change="refreshInheritViewAffiliates(inherits.affiliates_types_view)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.affiliates_types_view"
                      :disabled="AffiliatesViewColumnsDisabled"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="adminPermissions.affiliates_view_affiliates_visibility_teammates_id"
                        :disabled="AffiliatesViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_edit')" />

                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.affiliates.affiliates_types_edit') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('affiliates_types_edit')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.affiliates_types_edit"
                              :disabled="inheritIsDisabled('affiliates_types_edit')"
                              @change="refreshInheritEditAffiliates(inherits.affiliates_types_edit)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.affiliates_types_edit"
                      :disabled="AffiliatesEditColumnsDisabled"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="adminPermissions.affiliates_edit_affiliates_visibility_teammates_id"
                        :disabled="AffiliatesEditColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.affiliates_types_edit"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_sub_affiliates') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_sub_affiliates_edit_settings')" />

                  <div class="field field-input">
                    <span class="label">{{ $t('permissions.affiliates.affiliates_sub_affiliates_max_percent') }}</span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.affiliates_sub_affiliates_max_percent"
                          :disabled="inheritIsDisabled('affiliates_sub_affiliates_max_percent')"
                          @change="refreshInheritSubAff(inherits.affiliates_sub_affiliates_max_percent)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.affiliates_sub_affiliates_max_percent"
                        :disabled="SubAffiliatesValueDisabled"
                        :error="errors.affiliates_sub_affiliates_max_percent"
                        error-tooltip
                        width="72px"
                        post-text="%"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_actions_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_activity_log_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_can_login')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_payment_info_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_domain_change_notifications_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_reporting_settings_rs_profit_visibility_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_reporting_settings_cpa_profit_visibility_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_email_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_payments_can_create')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('payments_external_payouts_actions')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_media_buying_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_promo_codes_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_balance_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_rotation_groups_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_limits_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_traffic_block_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_kyc_request')" />
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_kyc_skip')" />
                </div>
              </div>
            </div>
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.offers.offers_catalog_card_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('offers_catalog_edit')" />
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.offers.title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('offers_edit')" />
                  <PermissionTreeCommon
                    v-if="!isCategoriesLoading"
                    v-model:mode="adminPermissions.offers_link_to_catalog_categories_allowed_mode"
                    v-model:ids="adminPermissions.offers_link_to_catalog_categories_allowed_list"
                    v-model:inherit="inherits.offers_link_to_catalog_categories_allowed_mode"
                    class="field"
                    :label="$t('permissions.offers.use_from_catalog')"
                    :disabled="offersDisabledCatalog"
                    :select-placeholder="$t('permissions.offers.select_categories')"
                    :field-list="dataCategories"
                    :permission-types="['allow_all', 'allow_selected', 'deny_selected']"
                    :show-inherit-checkbox="true"
                    @update:mode="onOffersCatalogModeChange"
                    @update:ids="onOffersCatalogIdsChange"
                    @update:inherit="onOffersCatalogInheritChange"
                  />
                  <Vnodes :vnodes="renderPermissionSwitchControl('offers_delete')" />
                </div>
              </div>

              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.offers.offers_revshare') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('offers_revshare_edit_settings')" />

                  <div class="field field-input">
                    <span class="label">{{ $t('permissions.offers.offers_revshare_max_percent_sport') }}</span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.offers_revshare_max_percent_sport"
                          :disabled="inheritIsDisabled('offers_revshare_max_percent_sport')"
                          @change="refreshInheritRevshareSport(inherits.offers_revshare_max_percent_sport)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_revshare_max_percent_sport"
                        :disabled="setRevShareDisabledSport"
                        :error="errors.offers_revshare_max_percent_sport"
                        error-tooltip
                        width="72px"
                        post-text="%"
                      />
                    </div>
                  </div>
                  <div class="field field-input">
                    <span class="label">{{ $t('permissions.offers.offers_revshare_max_percent_casino') }}</span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.offers_revshare_max_percent_casino"
                          :disabled="inheritIsDisabled('offers_revshare_max_percent_casino')"
                          @change="refreshInheritRevshareCasino(inherits.offers_revshare_max_percent_casino)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_revshare_max_percent_casino"
                        :disabled="setRevShareDisabledCasino"
                        :error="errors.offers_revshare_max_percent_casino"
                        error-tooltip
                        width="72px"
                        post-text="%"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.offers.offers_cpa') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('offers_cpa_edit_settings')" />
                  <div class="field field-input select">
                    <span class="label">{{ $t('permissions.offers.offers_cpa_max_payout_currency') }}</span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.offers_cpa_max_payout_currency_code"
                          :disabled="inheritIsDisabled('offers_cpa_max_payout_currency_code')"
                          @change="refreshInheritCpaMaxCurrency(inherits.offers_cpa_max_payout_currency_code)"
                        />
                      </div>
                      <UiSelectElement
                        v-model="adminPermissions.offers_cpa_max_payout_currency_code"
                        :placeholder="$t('permissions.offers.not_selected')"
                        :list="currencyOptions"
                        :disabled="setCpaDisabledMaxCurrencyCode"
                        filterable
                        width="72"
                        option-value-param="code"
                      />
                    </div>
                  </div>
                  <div class="field field-input">
                    <span class="label">{{ $t('permissions.offers.offers_cpa_max_payout') }}</span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.offers_cpa_max_payout"
                          :disabled="inheritIsDisabled('offers_cpa_max_payout')"
                          @change="refreshInheritCpaMax(inherits.offers_cpa_max_payout)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_cpa_max_payout"
                        :disabled="setCpaDisabledMax"
                        width="72px"
                      />
                    </div>
                  </div>
                  <div class="field field-input">
                    <span class="label">{{ $t('permissions.offers.offers_cpa_min_payout_deposits_sum_ratio') }}</span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.offers_cpa_min_payout_deposits_sum_ratio"
                          :disabled="inheritIsDisabled('offers_cpa_min_payout_deposits_sum_ratio')"
                          @change="refreshInheritCpaRatio(inherits.offers_cpa_min_payout_deposits_sum_ratio)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_cpa_min_payout_deposits_sum_ratio"
                        :disabled="setCpaDisabledRatio"
                        width="72px"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_notes') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_notes_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_notes_edit')" />
                </div>
              </div>

              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_security_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_password_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_2fa_can_reset')" />
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_status_change_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_can_approve')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_can_decline')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_can_block')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_can_unblock')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_can_revoke')" />
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_notifications') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_notifications_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_notifications_edit')" />
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.affiliates.affiliates_kpi') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('affiliates_kpi_bonuses_edit')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="$t('permissions.payments.title')"
            name="payments"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.payments.payments_card_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('payments_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('payments_review_actions')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('payments_chargebacks_actions')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('payments_make_manual_cpa_payouts')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="$t('permissions.players.title')"
            name="players"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.players.players_card_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('players_view')" />

                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.players.players_view_columns') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('players_available_property_groups')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.players_available_property_groups"
                              :disabled="inheritIsDisabled('players_available_property_groups')"
                              @change="refreshInheritPlayers(inherits.players_available_property_groups)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.players_available_property_groups"
                      :disabled="PlayersViewDisabledColumns"
                      :options="[
                        {
                          label: $t('permissions.players.subId'),
                          value: 'sub_id',
                        },
                        {
                          label: $t('permissions.players.country'),
                          value: 'country',
                        },
                        {
                          label: $t('permissions.players.personal_information'),
                          value: 'personal_information',
                        },
                        {
                          label: $t('permissions.players.affiliate'),
                          value: 'affiliate',
                        },
                        {
                          label: $t('permissions.players.contacts'),
                          value: 'contacts',
                        },
                        {
                          label: $t('permissions.players.visit'),
                          value: 'visit_information',
                        },
                      ]"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.players.players_card_other') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('players_virtual_edit_settings')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('players_assign_affiliate')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="$t('permissions.dms.title')"
            name="dms"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.dms.dns_hosting_accounts') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('dns_hosting_accounts_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('dns_hosting_accounts_edit')" />
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.dms.domain_registrar_accounts') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('domain_registrar_accounts_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('domain_registrar_accounts_edit')" />
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.dms.platform_servers') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_servers_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_servers_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_servers_can_run_jobs')" />
                </div>
              </div>
            </div>
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.dms.platform_service_domains') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_service_domains_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_service_domains_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_service_domains_can_run_jobs')" />
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.dms.service_rotation_groups') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('service_rotation_groups_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('service_rotation_groups_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('service_rotation_groups_domains_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('service_rotation_groups_domains_edit')" />
                </div>
              </div>
              <!-- /// -->
              <div class="card">
                <div class="title">
                  <span>{{ $t(`permissions.dms.dedicated_site_domains`) }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t(`permissions.inherit`) }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t(`permissions.status`) }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_betting_domains_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('platform_betting_domains_edit')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="$t('permissions.media_buying.title')"
            name="media-buying"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.media_buying.payments') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('media_buying_payments_view')" />
                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.media_buying.media_buying_payments_affiliates_types_view') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_payments_affiliates_types_view')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_affiliates_types_view"
                              :disabled="inheritIsDisabled('media_buying_payments_affiliates_types_view')"
                              @change="refreshInheritViewPayments(inherits.media_buying_payments_affiliates_types_view)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_affiliates_types_view"
                      :disabled="MediaBuyingPaymentsViewColumnsDisabled"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_payments_view_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPaymentsViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_payments_affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>

                  <Vnodes :vnodes="renderPermissionSwitchControl('media_buying_payments_review')" />

                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.media_buying.media_buying_payments_affiliates_types_review') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_payments_affiliates_types_review')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_affiliates_types_review"
                              :disabled="inheritIsDisabled('media_buying_payments_affiliates_types_review')"
                              @change="
                                refreshInheritEditPayments(inherits.media_buying_payments_affiliates_types_review)
                              "
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_affiliates_types_review"
                      :disabled="MediaBuyingPaymentsReviewColumnsDisabled"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_payments_review_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPaymentsReviewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_payments_affiliates_types_review"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.media_buying.payments_moderation') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.media_buying.statuses') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_payments_moderation_statuses_visible')"
                                :key="dependency"
                                :class="{
                                  'dependency-item': true,
                                  selected: (
                                    adminPermissions[dependency] === 'allow'
                                    && inherits[dependency] === false
                                  ),
                                }"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_moderation_statuses_visible"
                              :disabled="inheritIsDisabled('media_buying_payments_moderation_statuses_visible')"
                              @change="refreshInheritViewPaymentsModeration(
                                inherits.media_buying_payments_moderation_statuses_visible
                              )"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_moderation_statuses_visible"
                      :disabled="MediaBuyingPaymentsModerationColumnsDisabled"
                      :options="statusList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    />
                  </div>
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.media_buying.payments_payout') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.media_buying.statuses') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_payments_payout_statuses_visible')"
                                :key="dependency"
                                :class="{
                                  'dependency-item': true,
                                  selected: (
                                    adminPermissions[dependency] === 'allow'
                                    && inherits[dependency] === false
                                  ),
                                }
                                "
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_payout_statuses_visible"
                              :disabled="inheritIsDisabled('media_buying_payments_payout_statuses_visible')"
                              @change="
                                refreshInheritViewPaymentsPayout(inherits.media_buying_payments_payout_statuses_visible)
                              "
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_payout_statuses_visible"
                      :disabled="MediaBuyingPaymentsPayoutColumnsDisabled"
                      :options="[
                        {
                          label: $t('permissions.not_paid'),
                          value: 'not_paid',
                        },
                        {
                          label: $t('permissions.paid'),
                          value: 'paid',
                        },
                        {
                          label: $t('permissions.for_rework'),
                          value: 'for_rework',
                        },
                        {
                          label: $t('permissions.failed'),
                          value: 'failed',
                        },
                        {
                          label: $t('permissions.processing'),
                          value: 'processing',
                        },
                      ]"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.media_buying.posts') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('media_buying_posts_view')" />
                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.list.media_buying_posts_affiliates_types_view') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_posts_affiliates_types_view')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_posts_affiliates_types_view"
                              :disabled="inheritIsDisabled('media_buying_posts_affiliates_types_view')"
                              @change="refreshInheritViewPosts(inherits.media_buying_posts_affiliates_types_view)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_posts_affiliates_types_view"
                      :disabled="MediaBuyingPostsViewColumnsDisabled"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_posts_view_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPostsViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_posts_affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>

                  <Vnodes :vnodes="renderPermissionSwitchControl('media_buying_posts_review')" />

                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.list.media_buying_posts_affiliates_types_review') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_posts_affiliates_types_review')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_posts_affiliates_types_review"
                              :disabled="inheritIsDisabled('media_buying_posts_affiliates_types_review')"
                              @change="refreshInheritReviewPosts(inherits.media_buying_posts_affiliates_types_review)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_posts_affiliates_types_review"
                      :disabled="MediaBuyingPostsReviewColumnsDisabled"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_posts_review_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPostsReviewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_posts_affiliates_types_review"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
                <div class="card-content">
                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.media_buying.statuses') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_posts_statuses_visible')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_posts_statuses_visible"
                              :disabled="inheritIsDisabled('media_buying_posts_statuses_visible')"
                              @change="refreshInheritPostStatuses(inherits.media_buying_posts_statuses_visible)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_posts_statuses_visible"
                      :disabled="MediaBuyingPostsStatusesColumnsDisabled"
                      :options="statusList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    />
                  </div>
                </div>
              </div>
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.media_buying.reports') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <Vnodes :vnodes="renderPermissionSwitchControl('media_buying_reports_view')" />
                  <div class="field field-with-field">
                    <div class="field">
                      <span class="label">{{ $t('permissions.list.media_buying_reports_affiliates_types_view') }}</span>
                      <div class="values ">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('media_buying_reports_affiliates_types_view')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.media_buying_reports_affiliates_types_view"
                              :disabled="inheritIsDisabled('media_buying_reports_affiliates_types_view')"
                              @change="
                                refreshInheritViewMediaReports(inherits.media_buying_reports_affiliates_types_view)
                              "
                            />
                          </div>
                        </ElTooltip>
                        <div class="access-status" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_reports_affiliates_types_view"
                      :disabled="MediaBuyingReportsViewColumnsDisabled"
                      :options="partnersTypeList"
                      group-class-name="team-permissions__checkbox-group"
                      button-class-name="team-permissions__checkbox"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_reports_view_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingReportsViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_reports_affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="$t('permissions.crm.title')"
            name="crm"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.crm.segments') }}</span>
                  <span class="radio-block radio-block_allow">
                    {{ $t('permissions.inherit') }}
                  </span>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t(`permissions.crm.segments_view`) }} </span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.crm_segments_view"
                          @change="refreshInheritSegmentsView(inherits.crm_segments_view)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_segments_view"
                      :disabled="SegmentsViewTypeDisabled"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                      @change="checkSegmentPermission(adminPermissions.crm_segments_view, 'view')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          class="team-permissions__radio-button"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t(`permissions.crm.segments_edit`) }} </span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('crm_segments_edit')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.crm_segments_edit"
                              :disabled="SegmentsEditDisabled"
                              @change="refreshInheritSegmentsEdit(inherits.crm_segments_edit)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_segments_edit"
                      :disabled="SegmentsEditTypeDisabled"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                      @change="checkSegmentPermission(adminPermissions.crm_segments_edit, 'edit')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          class="team-permissions__radio-button"
                          :disabled="isDisabledOption('crm_segments_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t(`permissions.crm.segments_review`) }} </span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('crm_segments_review')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.crm_segments_review"
                              :disabled="SegmentsReviewDisabled"
                              @change="refreshInheritSegmentsReview(inherits.crm_segments_review)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_segments_review"
                      :disabled="SegmentsReviewTypeDisabled"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                      @change="checkSegmentPermission(adminPermissions.crm_segments_review, 'review')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          class="team-permissions__radio-button"
                          :disabled="isDisabledOption('crm_segments_edit', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
              </div>
            </div>
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.crm.activities') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t(`permissions.crm.activities_view`) }} </span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.crm_activities_view"
                          @change="refreshInheritActivitiesView(inherits.crm_activities_view)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_view"
                      :disabled="ActivitiesViewTypeDisabled"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                      @change="checkActivitiesPermission(adminPermissions.crm_activities_view, 'view')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          class="team-permissions__radio-button"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t(`permissions.crm.activities_edit`) }} </span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('crm_activities_edit')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.crm_activities_edit"
                              :disabled="ActivitiesEditDisabled"
                              @change="refreshInheritActivitiesEdit(inherits.crm_activities_edit)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_edit"
                      :disabled="ActivitiesEditTypeDisabled"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          class="team-permissions__radio-button"
                          :disabled="isDisabledOption('crm_activities_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t(`permissions.crm.activities_review`) }} </span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('crm_activities_review')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.crm_activities_review"
                              :disabled="ActivitiesReviewDisabled"
                              @change="refreshInheritActivitiesReview(inherits.crm_activities_review)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_review"
                      :disabled="ActivitiesReviewTypeDisabled"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          class="team-permissions__radio-button"
                          :disabled="isDisabledOption('crm_activities_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t(`permissions.crm.activities_run`) }} </span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="depends-on-header">
                                {{ $t('permissions.depends') }}
                              </h3>
                              <li
                                v-for="dependency in getPermissionDepends('crm_activities_run')"
                                :key="dependency"
                                :class="{'dependency-item': true, selected: (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ $t(`permissions.list.${dependency}`) }}
                              </li>
                            </div>
                          </template>

                          <div class="inherit-check-wrapper">
                            <UiCheckbox
                              v-model="inherits.crm_activities_run"
                              :disabled="ActivitiesRunDisabled"
                              @change="refreshInheritActivitiesRun(inherits.crm_activities_run)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_run"
                      :disabled="ActivitiesRunTypeDisabled"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          class="team-permissions__radio-button"
                          :disabled="isDisabledOption('crm_activities_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="$t('permissions.others.title')"
            name="others"
          >
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.tools.tools_promo_codes_checker') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.status') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <!--                  <Vnodes :vnodes="renderPermissionSwitchControl('tools_promo_codes_checker_view')" />-->

                  <Vnodes :vnodes="renderPermissionSwitchControl('tools_promo_landings_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('tools_promo_landings_edit')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('marketing_materials_view')" />

                  <Vnodes :vnodes="renderPermissionSwitchControl('marketing_materials_edit')" />
                </div>
              </div>
              <!--              <div class="card">-->
              <!--                <div class="title">-->
              <!--                  <span>{{ $t('permissions.live.live_card_title') }}</span>-->
              <!--                  <span>-->
              <!--                    <span class="radio-block radio-block_allow">-->
              <!--                      {{ $t('permissions.inherit') }}-->
              <!--                    </span>-->
              <!--                    <span class="radio-block radio-block_allow">-->
              <!--                      {{ $t('permissions.status') }}-->
              <!--                    </span>-->
              <!--                  </span>-->
              <!--                </div>-->
              <!--                <div class="card-content">-->
              <!--                  <Vnodes :vnodes="renderPermissionSwitchControl('last_bets_and_deposits_view')" />-->

              <!--                  <Vnodes :vnodes="renderPermissionSwitchControl('top_events_view')" />-->
              <!--                </div>-->
              <!--              </div>-->
            </div>
            <div class="permission-column">
              <div class="card">
                <div class="title">
                  <span>{{ $t('permissions.geo.geo_card_title') }}</span>
                  <span>
                    <span class="radio-block radio-block_allow">
                      {{ $t('permissions.inherit') }}
                    </span>
                  </span>
                </div>
                <div class="card-content">
                  <div class="field">
                    <span class="label">{{ $t('permissions.geo.geo_statistics') }}</span>
                    <div class="values ">
                      <div class="inherit-check-wrapper">
                        <UiCheckbox
                          v-model="inherits.countries_allowed_mode"
                          @change="refreshInheritGeo(inherits.countries_allowed_mode)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="field">
                    <UiRadioGroup
                      v-model="adminPermissions.countries_allowed_mode"
                      :disabled="GeoViewDisabled"
                      :options="[
                        {
                          label: $t('permissions.allow_all'),
                          value: 'allow_all',
                        },
                        {
                          label: $t('permissions.allow_selected'),
                          value: 'allow_selected',
                        },
                        {
                          label: $t('permissions.deny_all'),
                          value: 'deny_all',
                        },
                        {
                          label: $t('permissions.deny_selected'),
                          value: 'deny_selected',
                        }
                      ]"
                      class="team-permissions__radio-group-wrapper"
                      group-class-name="team-permissions__radio-group"
                      button-class-name="team-permissions__radio-button"
                      @change="checkGeoPermission(adminPermissions.countries_allowed_mode)"
                    >
                      <UiSelectElement
                        v-model="adminPermissions.countries_allowed_list"
                        :disabled="adminPermissions.countries_allowed_mode === 'allow_all' || adminPermissions.countries_allowed_mode === 'deny_all' || GeoViewDisabled"
                        :placeholder="$t('permissions.add_country')"
                        :list="countries"
                        size="big"
                        search-param="name"
                        option-value-param="code"
                        filterable
                        multiple
                        collapse-tag-tooltip
                        :max-collapse-tags="1"
                        :error="errors.countries_allowed_list"
                      />
                    </UiRadioGroup>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>
      <div class="foot t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          @click="close"
        >
          {{ $t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          filled
          icon="check"
          @click="save"
        >
          {{ $t('team_popup.save_button') }}
        </UiButton>
      </div>
    </div>
    <div class="wrapper" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { h, resolveComponent } from 'vue';
import { dependsOnConfig, requiresForConfig, resetFieldsConfig } from '@/configs/permissions';
import {
  applyPermissionsSnapshot,
  getInheritsObject,
  getInheritsSnapshot,
  getPermissionsObject,
  getPermissionsSnapshot,
  sortInheritsSnapshot,
  sortPermissionsSnapshot,
} from '@/helpers/permissions';
import PermissionTree from '@/components/PermissionTree.vue';
import PermissionTreeCommon from '@/components/PermissionTreeCommon.vue';
import _ from '@/setup/lodash';
import Vnodes from '@/components/VNodes.vue';
import sessionAction from '@/views/mixins/session-action';

export default {
  name: 'UserPermissions',
  components: {
    PermissionTreeCommon,
    PermissionTree,
    Vnodes,
  },
  mixins: [sessionAction],
  props: {
    userInfo: {
      type: Object,
      default() {
        return {};
      },
    },
    isOpen: {
      type: Boolean,
      required: true,
    },

    id: {
      type: String,
      default() {
        return '';
      },
    },
  },
  emits: ['close'],

  data() {
    return {
      activePermissions: ['reports'],
      isLoading: true,
      isCategoriesLoading: false,
      currentTeamSelect: '',
      value: true,
      activeColor: 'var(--text-primary)',
      inactiveColor: 'var(--text-negative)',

      users: null,

      crmWeight: {
        all: 3,
        teammates: 2,
        own: 1,
        deny: 0,
      },
      crmAllowOptions: {
        all: ['all', 'teammates', 'own', 'deny'],
        teammates: ['teammates', 'own', 'deny'],
        own: ['own', 'deny'],
        deny: ['deny'],
      },

      currencyOptions: [],

      dataCategories: {},
      teamAcl: {},
      postPermissions: {},
      errors: {},
      permissionSnapshots: {},
      inheritsSnapshots: {},
      savedGeoValues: {},

      adminPermissions: {
        last_bets_and_deposits_view: 'deny',
        top_events_view: 'deny',
        reports_view: 'deny',
        reports_group_by_affiliates: 'deny',
        reports_group_by_countries: 'deny',
        reports_group_by_sub_affiliates: 'deny',
        reports_group_by_payments: 'deny',
        reports_view_revenue_columns: 'deny',
        reports_view_profit_columns: 'deny',
        reports_view_deposits_sum_columns: 'deny',
        reports_view_deposits_count_columns: 'deny',
        reports_view_withdrawals_sum_columns: 'deny',
        reports_view_withdrawals_count_columns: 'deny',
        reports_view_sport_bets_sum_columns: 'deny',
        reports_view_sport_bets_count_columns: 'deny',
        reports_view_casino_bets_sum_columns: 'deny',
        reports_view_casino_bets_count_columns: 'deny',
        affiliates_view: 'deny',
        affiliates_edit: 'deny',
        affiliates_rotation_groups_edit: 'deny',
        affiliates_sub_affiliates_edit_settings: 'deny',
        offers_catalog_edit: 'deny',
        offers_edit: 'deny',
        offers_delete: 'deny',
        offers_revshare_edit_settings: 'deny',
        offers_cpa_edit_settings: 'deny',
        payments_view: 'deny',
        payments_review_actions: 'deny',
        payments_chargebacks_actions: 'deny',
        payments_make_manual_cpa_payouts: 'deny',
        players_view: 'deny',
        players_virtual_edit_settings: 'deny',
        players_assign_affiliate: 'deny',
        tools_promo_codes_checker_view: 'deny',
        tools_promo_landings_view: 'deny',
        tools_promo_landings_edit: 'deny',
        marketing_materials_view: 'deny',
        marketing_materials_edit: 'deny',

        media_buying_payments_view: 'deny',
        media_buying_payments_affiliates_types_view: [],
        media_buying_payments_review: 'deny',
        media_buying_payments_affiliates_types_review: [],
        media_buying_payments_moderation_statuses_visible: [],
        media_buying_payments_payout_statuses_visible: [],
        media_buying_posts_view: 'deny',
        media_buying_posts_affiliates_types_view: [],
        media_buying_posts_review: 'deny',
        media_buying_posts_affiliates_types_review: [],
        media_buying_posts_statuses_visible: [],
        media_buying_reports_view: 'deny',
        media_buying_reports_affiliates_types_view: [],

        media_buying_posts_view_affiliates_visibility_teammates_id: [],
        media_buying_posts_review_affiliates_visibility_teammates_id: [],
        media_buying_payments_view_affiliates_visibility_teammates_id: [],
        media_buying_payments_review_affiliates_visibility_teammates_id: [],
        media_buying_reports_view_affiliates_visibility_teammates_id: [],

        countries_allowed_mode: 'allow_all',
        countries_allowed_list: [],

        reports_affiliates_types: [],
        reports_view_affiliates_visibility_teammates_id: [],

        affiliates_types_view: [],
        affiliates_view_affiliates_visibility_teammates_id: [],

        affiliates_types_edit: [],
        affiliates_edit_affiliates_visibility_teammates_id: [],

        players_available_property_groups: [],
        affiliates_sub_affiliates_max_percent: 0,
        offers_revshare_max_percent_sport: 0,
        offers_revshare_max_percent_casino: 0,
        offers_cpa_max_payout: 0,
        offers_cpa_max_payout_currency_code: null,
        offers_cpa_min_payout_deposits_sum_ratio: 0,

        offers_link_to_catalog_categories_allowed_mode: 'allow_all',
        offers_link_to_catalog_categories_allowed_list: [],

        dns_hosting_accounts_view: 'deny',
        dns_hosting_accounts_edit: 'deny',
        domain_registrar_accounts_view: 'deny',
        domain_registrar_accounts_edit: 'deny',
        platform_servers_view: 'deny',
        platform_servers_edit: 'deny',
        platform_servers_can_run_jobs: 'deny',
        platform_service_domains_view: 'deny',
        platform_service_domains_edit: 'deny',
        platform_service_domains_can_run_jobs: 'deny',
        platform_betting_domains_view: 'deny',
        platform_betting_domains_edit: 'deny',
        service_rotation_groups_view: 'deny',
        service_rotation_groups_edit: 'deny',
        service_rotation_groups_domains_view: 'deny',
        service_rotation_groups_domains_edit: 'deny',

        affiliates_activity_log_view: 'deny',
        affiliates_notes_view: 'deny',
        affiliates_notes_edit: 'deny',

        affiliates_can_login: 'deny',
        affiliates_password_edit: 'deny',
        affiliates_payment_info_edit: 'deny',
        affiliates_domain_change_notifications_edit: 'deny',
        affiliates_kyc_request: 'deny',
        affiliates_kyc_skip: 'deny',
        affiliates_can_approve: 'deny',
        affiliates_can_decline: 'deny',
        affiliates_can_block: 'deny',
        affiliates_can_unblock: 'deny',
        affiliates_can_revoke: 'deny',
        affiliates_reporting_settings_rs_profit_visibility_edit: 'deny',
        affiliates_reporting_settings_cpa_profit_visibility_edit: 'deny',
        affiliates_email_edit: 'deny',
        affiliates_payments_can_create: 'deny',
        payments_external_payouts_actions: 'deny',
        affiliates_2fa_can_reset: 'Reset 2FA',
        affiliates_media_buying_edit: 'deny',
        affiliates_promo_codes_edit: 'deny',
        affiliates_balance_edit: 'deny',
        affiliates_limits_edit: 'deny',
        affiliates_traffic_block_edit: 'deny',

        crm_segments_view: 'deny',
        crm_segments_review: 'deny',
        crm_segments_edit: 'deny',
        crm_activities_view: 'deny',
        crm_activities_review: 'deny',
        crm_activities_edit: 'deny',
        crm_activities_run: 'deny',

        affiliates_notifications_view: 'deny',
        affiliates_notifications_edit: 'deny',
        affiliates_kpi_bonuses_edit: 'deny',
      },
      inherits: {
        last_bets_and_deposits_view: true,
        top_events_view: true,
        reports_view: true,
        reports_group_by_affiliates: true,
        reports_group_by_countries: true,
        reports_group_by_sub_affiliates: true,
        reports_group_by_payments: true,
        reports_view_revenue_columns: true,
        reports_view_profit_columns: true,
        reports_view_deposits_sum_columns: true,
        reports_view_deposits_count_columns: true,
        reports_view_withdrawals_sum_columns: true,
        reports_view_withdrawals_count_columns: true,
        reports_view_sport_bets_sum_columns: true,
        reports_view_sport_bets_count_columns: true,
        reports_view_casino_bets_sum_columns: true,
        reports_view_casino_bets_count_columns: true,
        affiliates_view: true,
        affiliates_edit: true,
        affiliates_rotation_groups_edit: true,
        affiliates_sub_affiliates_edit_settings: true,
        offers_catalog_edit: true,
        offers_edit: true,
        offers_delete: true,
        offers_revshare_edit_settings: true,
        offers_cpa_edit_settings: true,
        payments_view: true,
        payments_review_actions: true,
        payments_chargebacks_actions: true,
        payments_make_manual_cpa_payouts: true,
        players_view: true,
        players_virtual_edit_settings: true,
        players_assign_affiliate: true,
        tools_promo_codes_checker_view: true,
        tools_promo_landings_view: true,
        tools_promo_landings_edit: true,
        marketing_materials_view: true,
        marketing_materials_edit: true,
        reports_affiliates_types: true,
        reports_view_affiliates_visibility_teammates_id: true,

        offers_link_to_catalog_categories_allowed_mode: true,
        offers_link_to_catalog_categories_allowed_list: true,

        affiliates_view_affiliates_visibility_teammates_id: true,
        affiliates_edit_affiliates_visibility_teammates_id: true,
        media_buying_posts_view_affiliates_visibility_teammates_id: true,
        media_buying_posts_review_affiliates_visibility_teammates_id: true,
        media_buying_payments_view_affiliates_visibility_teammates_id: true,
        media_buying_payments_review_affiliates_visibility_teammates_id: true,
        media_buying_reports_view_affiliates_visibility_teammates_id: true,

        affiliates_types_view: true,
        affiliates_types_edit: true,
        countries_allowed_mode: true,
        countries_allowed_list: true,

        media_buying_payments_view: true,
        media_buying_payments_affiliates_types_view: true,
        media_buying_payments_review: true,
        media_buying_payments_affiliates_types_review: true,
        media_buying_payments_moderation_statuses_visible: true,
        media_buying_payments_payout_statuses_visible: true,
        media_buying_posts_view: true,
        media_buying_posts_affiliates_types_view: true,
        media_buying_posts_review: true,
        media_buying_posts_affiliates_types_review: true,
        media_buying_posts_statuses_visible: true,
        media_buying_reports_view: true,
        media_buying_reports_affiliates_types_view: true,

        players_available_property_groups: [],
        affiliates_sub_affiliates_max_percent: 0,
        offers_revshare_max_percent_sport: 0,
        offers_revshare_max_percent_casino: 0,
        offers_cpa_max_payout: 0,
        offers_cpa_max_payout_currency_code: null,
        offers_cpa_min_payout_deposits_sum_ratio: 0,

        dns_hosting_accounts_view: true,
        dns_hosting_accounts_edit: true,
        domain_registrar_accounts_view: true,
        domain_registrar_accounts_edit: true,
        platform_servers_view: true,
        platform_servers_edit: true,
        platform_servers_can_run_jobs: true,
        platform_service_domains_view: true,
        platform_service_domains_edit: true,
        platform_service_domains_can_run_jobs: true,
        platform_betting_domains_view: true,
        platform_betting_domains_edit: true,
        service_rotation_groups_view: true,
        service_rotation_groups_edit: true,
        service_rotation_groups_domains_view: true,
        service_rotation_groups_domains_edit: true,

        affiliates_activity_log_view: true,
        affiliates_notes_view: true,
        affiliates_notes_edit: true,

        affiliates_can_login: true,
        affiliates_password_edit: true,
        affiliates_payment_info_edit: true,
        affiliates_domain_change_notifications_edit: true,
        affiliates_kyc_request: true,
        affiliates_kyc_skip: true,
        affiliates_can_approve: true,
        affiliates_can_decline: true,
        affiliates_can_block: true,
        affiliates_can_unblock: true,
        affiliates_can_revoke: true,
        affiliates_reporting_settings_rs_profit_visibility_edit: true,
        affiliates_reporting_settings_cpa_profit_visibility_edit: true,
        affiliates_email_edit: true,
        affiliates_payments_can_create: true,
        payments_external_payouts_actions: true,
        affiliates_2fa_can_reset: true,
        affiliates_media_buying_edit: true,
        affiliates_promo_codes_edit: true,
        affiliates_balance_edit: true,
        affiliates_limits_edit: true,
        affiliates_traffic_block_edit: true,

        crm_segments_view: true,
        crm_segments_review: true,
        crm_segments_edit: true,
        crm_activities_view: true,
        crm_activities_review: true,
        crm_activities_edit: true,
        crm_activities_run: true,

        affiliates_notifications_view: true,
        affiliates_notifications_edit: true,
        affiliates_kpi_bonuses_edit: true,
      },
    };
  },

  computed: {
    ...mapGetters({
      countries: 'misc/countries',
    }),


    partnersTypeList() {
      return [
        {
          label: this.$t('permissions.independent'),
          value: 'independent',
        },
        {
          label: this.$t('permissions.other_teams_affiliates'),
          value: 'other_teams_affiliates',
        },
        {
          label: this.$t('permissions.own_affiliates'),
          value: 'own_affiliates',
        },
      ];
    },

    statusList() {
      return [
        {
          label: this.$t('permissions.on_review'),
          value: 'on_review',
        },
        {
          label: this.$t('permissions.approved'),
          value: 'approved',
        },
        {
          label: this.$t('permissions.for_rework'),
          value: 'for_rework',
        },
        {
          label: this.$t('permissions.declined'),
          value: 'declined',
        },
      ];
    },

    crmRadioList() {
      return [
        {
          label: this.$t('permissions.all'),
          value: 'all',
        },
        {
          label: this.$t('permissions.own'),
          value: 'own',
        },
        {
          label: this.$t('permissions.teammates'),
          value: 'teammates',
        },
        {
          label: this.$t('permissions.deny'),
          value: 'deny',
        },
      ];
    },

    teamId: {
      get() {
        return this.currentTeamSelect ? this.currentTeamSelect : '';
      },
      set(newVal) {
        this.currentTeamSelect = newVal;
      },
    },

    userTeams: {
      get() {
        return this.teamAcl ? this.teamAcl.teams : {};
      },
    },

    computedAcl: {
      get() {
        return this.teamAcl && this.teamAcl.permissions ? this.$_.cloneDeep(this.teamAcl.permissions[this.teamId]) : {};
      },
    },

    reportsDisabled: {
      get() {
        return this.inherits.reports_view === false && this.adminPermissions.reports_view === 'deny';
      },
    },

    reportsDisabledColumns: {
      get() {
        return this.reportsDisabled || this.inherits.reports_affiliates_types === true;
      },
    },

    offersDisabledCatalog: {
      get() {
        return this.inherits.offers_edit === false && this.adminPermissions.offers_edit === 'deny';
      },
    },

    MediaBuyingPaymentsViewDisabled: {
      get() {
        return this.inherits.media_buying_payments_view === false
          && this.adminPermissions.media_buying_payments_view === 'deny';
      },
    },

    MediaBuyingPaymentsViewColumnsDisabled: {
      get() {
        return this.MediaBuyingPaymentsViewDisabled
          || this.inherits.media_buying_payments_affiliates_types_view === true;
      },
    },

    MediaBuyingPaymentsReviewDisabled: {
      get() {
        return this.MediaBuyingPaymentsViewDisabled
          || (
            this.inherits.media_buying_payments_review === false
            && this.adminPermissions.media_buying_payments_review === 'deny'
          );
      },
    },

    MediaBuyingPaymentsReviewColumnsDisabled: {
      get() {
        return this.MediaBuyingPaymentsReviewDisabled
          || this.inherits.media_buying_payments_affiliates_types_review === true;
      },
    },

    MediaBuyingPaymentsModerationColumnsDisabled: {
      get() {
        return this.MediaBuyingPaymentsViewDisabled
          || this.inherits.media_buying_payments_moderation_statuses_visible === true;
      },
    },

    MediaBuyingPaymentsPayoutColumnsDisabled: {
      get() {
        return this.MediaBuyingPaymentsViewDisabled
          || this.inherits.media_buying_payments_payout_statuses_visible === true;
      },
    },

    MediaBuyingPostsViewDisabled: {
      get() {
        return this.inherits.media_buying_posts_view === false
          && this.adminPermissions.media_buying_posts_view === 'deny';
      },
    },

    MediaBuyingPostsViewColumnsDisabled: {
      get() {
        return this.MediaBuyingPostsViewDisabled || this.inherits.media_buying_posts_affiliates_types_view === true;
      },
    },

    MediaBuyingReportsViewDisabled: {
      get() {
        return this.inherits.media_buying_reports_view === false
          && this.adminPermissions.media_buying_reports_view === 'deny';
      },
    },
    MediaBuyingReportsViewColumnsDisabled: {
      get() {
        return this.MediaBuyingReportsViewDisabled || this.inherits.media_buying_reports_affiliates_types_view === true;
      },
    },

    MediaBuyingPostsReviewDisabled: {
      get() {
        return this.MediaBuyingPostsViewDisabled
          || (
            this.inherits.media_buying_posts_review === false
            && this.adminPermissions.media_buying_posts_review === 'deny'
          );
      },
    },

    MediaBuyingPostsReviewColumnsDisabled: {
      get() {
        return this.MediaBuyingPostsReviewDisabled || this.inherits.media_buying_posts_affiliates_types_review === true;
      },
    },

    MediaBuyingPostsStatusesColumnsDisabled: {
      get() {
        return this.MediaBuyingPostsViewDisabled || this.inherits.media_buying_posts_statuses_visible === true;
      },
    },

    AffiliatesViewColumnsDisabled: {
      get() {
        return this.AffiliatesViewDisabled || this.inherits.affiliates_types_view === true;
      },
    },

    AffiliatesViewDisabled: {
      get() {
        return this.inherits.affiliates_view === false && this.adminPermissions.affiliates_view === 'deny';
      },
    },

    AffiliatesEditDisabled: {
      get() {
        return this.AffiliatesViewDisabled
          || (
            this.inherits.affiliates_edit === false
            && this.adminPermissions.affiliates_edit === 'deny'
          );
      },
    },

    AffiliatesEditColumnsDisabled: {
      get() {
        return this.AffiliatesEditDisabled || this.inherits.affiliates_types_edit === true;
      },
    },

    SubAffiliatesEditDisabled: {
      get() {
        return this.AffiliatesEditDisabled || (this.inherits.affiliates_sub_affiliates_edit_settings === false
            && this.adminPermissions.affiliates_sub_affiliates_edit_settings === 'deny');
      },
    },

    SubAffiliatesValueDisabled: {
      get() {
        return this.SubAffiliatesEditDisabled || this.inherits.affiliates_sub_affiliates_max_percent === true;
      },
    },

    OffersEditDisabled: {
      get() {
        return this.AffiliatesEditDisabled || (
          this.inherits.offers_edit === false && this.adminPermissions.offers_edit === 'deny');
      },
    },

    setRevShareDisabled: {
      get() {
        return this.OffersEditDisabled
          || (
            this.inherits.offers_revshare_edit_settings === false
            && this.adminPermissions.offers_revshare_edit_settings === 'deny'
          );
      },
    },

    setRevShareDisabledSport: {
      get() {
        return this.setRevShareDisabled || this.inherits.offers_revshare_max_percent_sport === true;
      },
    },

    setRevShareDisabledCasino: {
      get() {
        return this.setRevShareDisabled || this.inherits.offers_revshare_max_percent_casino === true;
      },
    },

    setCpaDisabled: {
      get() {
        return this.OffersEditDisabled
          || (
            this.inherits.offers_cpa_edit_settings === false
            && this.adminPermissions.offers_cpa_edit_settings === 'deny'
          );
      },
    },

    setCpaDisabledMaxCurrencyCode: {
      get() {
        return this.setCpaDisabled || this.inherits.offers_cpa_max_payout_currency_code === true;
      },
    },

    setCpaDisabledMax: {
      get() {
        return this.setCpaDisabled || this.inherits.offers_cpa_max_payout === true;
      },
    },

    setCpaDisabledRatio: {
      get() {
        return this.setCpaDisabled || this.inherits.offers_cpa_min_payout_deposits_sum_ratio === true;
      },
    },

    PlayersViewDisabled: {
      get() {
        return this.inherits.players_view === false && this.adminPermissions.players_view === 'deny';
      },
    },

    PlayersViewDisabledColumns: {
      get() {
        return this.PlayersViewDisabled || this.inherits.players_available_property_groups === true;
      },
    },

    PaymentsViewDisabled: {
      get() {
        return this.inherits.payments_view === false && this.adminPermissions.payments_view === 'deny';
      },
    },

    LandingViewDisabled: {
      get() {
        return this.inherits.tools_promo_landings_view === false
          && this.adminPermissions.tools_promo_landings_view === 'deny';
      },
    },

    GeoViewDisabled: {
      get() {
        return this.inherits.countries_allowed_mode === true;
      },
    },


    SegmentsViewTypeDisabled: {
      get() {
        return this.inherits.crm_segments_view === true;
      },
    },
    SegmentsEditDisabled: {
      get() {
        return this.SegmentsViewTypeDisabled || this.adminPermissions.crm_segments_view === 'deny';
      },
    },
    SegmentsEditTypeDisabled: {
      get() {
        return this.SegmentsEditDisabled || this.inherits.crm_segments_edit === true;
      },
    },
    SegmentsReviewDisabled: {
      get() {
        return this.SegmentsEditTypeDisabled || this.adminPermissions.crm_segments_edit === 'deny';
      },
    },
    SegmentsReviewTypeDisabled: {
      get() {
        return this.SegmentsReviewDisabled || this.inherits.crm_segments_review === true;
      },
    },

    ActivitiesViewTypeDisabled: {
      get() {
        return this.inherits.crm_activities_view === true;
      },
    },
    ActivitiesEditDisabled: {
      get() {
        return this.ActivitiesViewTypeDisabled || this.adminPermissions.crm_activities_view === 'deny';
      },
    },
    ActivitiesEditTypeDisabled: {
      get() {
        return this.ActivitiesEditDisabled || this.inherits.crm_activities_edit === true;
      },
    },
    ActivitiesReviewDisabled: {
      get() {
        return this.ActivitiesViewTypeDisabled || this.adminPermissions.crm_activities_view === 'deny';
      },
    },
    ActivitiesReviewTypeDisabled: {
      get() {
        return this.ActivitiesReviewDisabled || this.inherits.crm_activities_review === true;
      },
    },
    ActivitiesRunDisabled: {
      get() {
        return this.ActivitiesViewTypeDisabled || this.adminPermissions.crm_activities_view === 'deny';
      },
    },
    ActivitiesRunTypeDisabled: {
      get() {
        return this.ActivitiesRunDisabled || this.inherits.crm_activities_run === true;
      },
    },
  },
  mounted() {
    this.onSessionUnlock = this.initData;
  },
  watch: {
    isOpen(v) {
      if (v) {
        this.initData();
        this.$refs.scroll.scrollTop = 0;
        document.querySelector('.user-permissions .permissions-wrapper')
          .addEventListener('scroll', this.hiddenSelectOptions);
      } else {
        document.querySelector('.user-permissions .permissions-wrapper')
          .removeEventListener('scroll', this.hiddenSelectOptions);
      }
    },
    currentTeamSelect() {
      this.getAdminPermissions();

      if (this.inherits.reports_affiliates_types === true) {
        this.refreshInheritReports(true);
      }

      if (this.inherits.offers_revshare_max_percent_sport === true) {
        this.refreshInheritRevshareSport(true);
      }

      if (this.inherits.offers_revshare_max_percent_casino === true) {
        this.refreshInheritRevshareCasino(true);
      }

      if (this.inherits.offers_cpa_max_payout === true) {
        this.refreshInheritCpaMax(true);
      }

      if (this.inherits.offers_cpa_max_payout_currency_code === true) {
        this.refreshInheritCpaMaxCurrency(true);
      }

      if (this.inherits.offers_cpa_min_payout_deposits_sum_ratio === true) {
        this.refreshInheritCpaRatio(true);
      }

      if (this.inherits.affiliates_sub_affiliates_max_percent === true) {
        this.refreshInheritSubAff(true);
      }

      if (this.inherits.affiliates_types_view === true) {
        this.refreshInheritViewAffiliates(true);
      }

      if (this.inherits.media_buying_payments_affiliates_types_view === true) {
        this.refreshInheritViewPayments(true);
      }

      if (this.inherits.media_buying_payments_moderation_statuses_visible === true) {
        this.refreshInheritViewPaymentsModeration(true);
      }
      if (this.inherits.media_buying_payments_payout_statuses_visible === true) {
        this.refreshInheritViewPaymentsPayout(true);
      }

      if (this.inherits.media_buying_payments_affiliates_types_review === true) {
        this.refreshInheritEditPayments(true);
      }

      if (this.inherits.media_buying_posts_affiliates_types_view === true) {
        this.refreshInheritViewPosts(true);
      }
      if (this.inherits.media_buying_reports_affiliates_types_view === true) {
        this.refreshInheritViewMediaReports(true);
      }
      if (this.inherits.media_buying_posts_affiliates_types_review === true) {
        this.refreshInheritReviewPosts(true);
      }
      if (this.inherits.media_buying_posts_statuses_visible === true) {
        this.refreshInheritPostStatuses(true);
      }

      if (this.inherits.affiliates_types_edit === true) {
        this.refreshInheritEditAffiliates(true);
      }

      if (this.inherits.players_available_property_groups === true) {
        this.refreshInheritPlayers(true);
      }

      if (this.inherits.countries_allowed_mode === true) {
        this.refreshInheritGeo(true);
      }

      if (this.inherits.offers_link_to_catalog_categories_allowed_mode === true) {
        this.refreshInheritCatalog(true);
      }
    },
    'adminPermissions.crm_segments_view': {
      handler() {
        this.checkWeight('crm_segments_view', 'crm_segments_edit');
      },
      deep: true,
    },
    'adminPermissions.crm_segments_edit': {
      handler() {
        this.checkWeight('crm_segments_edit', 'crm_segments_review');
      },
      deep: true,
    },
    'adminPermissions.crm_activities_view': {
      handler() {
        this.checkWeight('crm_activities_view', 'crm_activities_edit');
        this.checkWeight('crm_activities_view', 'crm_activities_review');
        this.checkWeight('crm_activities_view', 'crm_activities_run');
      },
      deep: true,
    },
  },
  methods: {
    initData() {
      if (this.isOpen) {
        this.getCategories();
        this.getAdminAcl();
      }
    },
    getUsers() {
      this.isLoading = true;
      this.errors = {};
      const _query = {
        team_id: this.teamId,
      };
      this.$api.getAdmins(_query).then((response) => {
        this.users = response.data.payload;
      })
        .catch((error) => {
          this.errors = this.$_.cloneDeep(error.data.errors);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    hiddenSelectOptions() {
      this.$refs.selectClose.$refs.uiSelectElement.handleEsc();
    },

    checkGeoPermission(permission) {
      this.errors = {};
      if (permission === 'allow_all' || permission === 'deny_all') {
        this.adminPermissions.countries_allowed_list = [];
      }
    },
    checkSegmentPermission(permission, type) {
      if (permission === 'deny') {
        if (type === 'view') {
          this.inherits.crm_segments_edit = true;
          this.refreshInheritSegmentsEdit(true);
        }
        if (type === 'edit') {
          this.inherits.crm_segments_review = true;
          this.refreshInheritSegmentsReview(true);
        }
      }
    },
    checkActivitiesPermission(permission, type) {
      if (permission === 'deny') {
        if (type === 'view') {
          this.inherits.crm_activities_edit = true;
          this.inherits.crm_activities_review = true;
          this.inherits.crm_activities_run = true;

          this.refreshInheritActivitiesEdit(true);
        }
        if (type === 'edit') {
          this.inherits.crm_activities_review = true;
          this.refreshInheritActivitiesReview(true);
        }
        if (type === 'review') {
          this.inherits.crm_activities_run = true;
          this.refreshInheritActivitiesRun(true);
        }
      }
    },

    getAdminPermissions: _.debounce(function getAdminPermissions() {
      this.$api.getAdminPermissions(this.userInfo.id, this.teamId)
        .then((response) => {
          this.adminPermissions = response.data.payload;
          const includeArray = [
            'crm_activities_edit',
            'crm_activities_review',
            'crm_activities_run',
            'crm_activities_view',
            'crm_segments_edit',
            'crm_segments_review',
            'crm_segments_view',
            'countries_allowed_mode',
            'countries_allowed_list',
            'reports_affiliates_types',
            'reports_view_affiliates_visibility_teammates_id',
            'affiliates_view_affiliates_visibility_teammates_id',
            'affiliates_edit_affiliates_visibility_teammates_id',
            'media_buying_posts_view_affiliates_visibility_teammates_id',
            'media_buying_posts_review_affiliates_visibility_teammates_id',
            'media_buying_payments_view_affiliates_visibility_teammates_id',
            'media_buying_payments_review_affiliates_visibility_teammates_id',
            'media_buying_reports_view_affiliates_visibility_teammates_id',
            'affiliates_types_view',
            'affiliates_types_edit',
            'media_buying_payments_affiliates_types_view',
            'media_buying_payments_affiliates_types_review',
            'media_buying_payments_moderation_statuses_visible',
            'media_buying_payments_payout_statuses_visible',
            'media_buying_posts_affiliates_types_view',
            'media_buying_posts_affiliates_types_review',
            'media_buying_reports_affiliates_types_view',
            'media_buying_posts_statuses_visible',
            'players_available_property_groups',
            'affiliates_sub_affiliates_max_percent',
            'offers_revshare_max_percent_sport',
            'offers_revshare_max_percent_casino',
            'offers_cpa_max_payout',
            'offers_cpa_max_payout_currency_code',
            'offers_cpa_min_payout_deposits_sum_ratio',
            'offers_link_to_catalog_categories_allowed_mode',
            'offers_link_to_catalog_categories_allowed_list',
          ];
          this.$_.forOwn(this.adminPermissions, (value, key) => {
            if (value == null) {
              this.inherits[key] = true;
              if (includeArray.indexOf(key) !== -1) {
                this.adminPermissions[key] = this.$_.cloneDeep(this.computedAcl[key]);
              }
            } else {
              this.inherits[key] = false;
            }
          });

          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeep(error.data.errors);
        });
    }, 50),

    onOffersCatalogModeChange(val) {
      if (val === 'allow_all') {
        this.adminPermissions.offers_link_to_catalog_categories_allowed_list = [];
      }

      if (val === null) {
        this.adminPermissions.offers_link_to_catalog_categories_allowed_list = null;
      }
    },

    onOffersCatalogIdsChange(val) {
      if (this.adminPermissions.offers_link_to_catalog_categories_allowed_mode === 'allow_all' && val && this.$_.size(val) > 0) {
        this.adminPermissions.offers_link_to_catalog_categories_allowed_mode = 'allow_selected';
      }
    },

    onOffersCatalogInheritChange(val) {
      this.inherits.offers_link_to_catalog_categories_allowed_list = val;
      if (val === true) {
        this.refreshInheritCatalog(true);
      }
    },

    getCategories() {
      this.isCategoriesLoading = true;
      this.$api.getAffiliateOffersCatalogCategories().then((res) => {
        this.dataCategories = res.data.payload;
      }).finally(() => {
        this.isCategoriesLoading = false;
      });
    },
    getAdminAcl() {
      this.$api.getAdminAcl(this.userInfo.id)
        .then((response) => {
          this.teamAcl = this.$_.cloneDeep(response.data.payload);
          this.currentTeamSelect = this.id ? this.id : this.userTeams[0].id;
          this.getUsers();
          this.getAdminPermissions();
          this.getCurrencies();
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeep(error.data.errors || {});
        });
    },

    getCurrencies() {
      this.$api.getMiscCustomerCurrencies().then((res) => {
        this.currencyOptions = res.data.payload;
      });
    },

    refreshInheritReports(val) {
      if (val === true) {
        this.adminPermissions.reports_affiliates_types
          = this.$_.cloneDeep(this.computedAcl.reports_affiliates_types);
        this.adminPermissions.reports_view_affiliates_visibility_teammates_id
          = this.$_.cloneDeep(this.computedAcl.reports_view_affiliates_visibility_teammates_id);
      }
    },

    refreshInheritCpaMaxCurrency(val) {
      if (val === true) {
        this.adminPermissions.offers_cpa_max_payout_currency_code
          = this.computedAcl.offers_cpa_max_payout_currency_code;
      }
    },

    refreshInheritCpaMax(val) {
      if (val === true) {
        this.adminPermissions.offers_cpa_max_payout = this.computedAcl.offers_cpa_max_payout;
      }
    },

    refreshInheritCpaRatio(val) {
      if (val === true) {
        this.adminPermissions.offers_cpa_min_payout_deposits_sum_ratio
          = this.computedAcl.offers_cpa_min_payout_deposits_sum_ratio;
      }
    },

    refreshInheritRevshareSport(val) {
      if (val === true) {
        this.adminPermissions.offers_revshare_max_percent_sport = this.computedAcl.offers_revshare_max_percent_sport;
      }
    },

    refreshInheritRevshareCasino(val) {
      if (val === true) {
        this.adminPermissions.offers_revshare_max_percent_casino = this.computedAcl.offers_revshare_max_percent_casino;
      }
    },

    refreshInheritSubAff(val) {
      if (val === true) {
        this.adminPermissions.affiliates_sub_affiliates_max_percent
          = this.computedAcl.affiliates_sub_affiliates_max_percent;
      }
    },

    refreshInheritEditAffiliates(val) {
      if (val === true) {
        this.adminPermissions.affiliates_types_edit = this.computedAcl.affiliates_types_edit;
        this.adminPermissions.affiliates_edit_affiliates_visibility_teammates_id
          = this.computedAcl.affiliates_edit_affiliates_visibility_teammates_id;
      }
    },

    refreshInheritViewAffiliates(val) {
      if (val === true) {
        this.adminPermissions.affiliates_types_view = this.computedAcl.affiliates_types_view;
        this.adminPermissions.affiliates_view_affiliates_visibility_teammates_id
          = this.computedAcl.affiliates_view_affiliates_visibility_teammates_id;
      }
    },
    refreshInheritSegmentsView(val) {
      if (val === true) {
        this.adminPermissions.crm_segments_view = this.computedAcl.crm_segments_view;
        this.inherits.crm_segments_edit = true;
        this.refreshInheritSegmentsEdit(true);
      }
    },
    refreshInheritSegmentsEdit(val) {
      if (val === true) {
        this.adminPermissions.crm_segments_edit = this.computedAcl.crm_segments_edit;
        this.inherits.crm_segments_review = true;
        this.refreshInheritSegmentsReview(true);
      }
    },
    refreshInheritSegmentsReview(val) {
      if (val === true) {
        this.adminPermissions.crm_segments_review = this.computedAcl.crm_segments_review;
      }
    },


    refreshInheritActivitiesView(val) {
      if (val === true) {
        this.adminPermissions.crm_activities_view = this.computedAcl.crm_activities_view;
        this.inherits.crm_activities_edit = true;
        this.inherits.crm_activities_review = true;
        this.inherits.crm_activities_run = true;
        this.refreshInheritActivitiesEdit(true);
      }
    },
    refreshInheritActivitiesEdit(val) {
      if (val === true) {
        this.adminPermissions.crm_activities_edit = this.computedAcl.crm_activities_edit;
      }
    },
    refreshInheritActivitiesReview(val) {
      if (val === true) {
        this.adminPermissions.crm_activities_review = this.computedAcl.crm_activities_review;
      }
    },
    refreshInheritActivitiesRun(val) {
      if (val === true) {
        this.adminPermissions.crm_activities_run = this.computedAcl.crm_activities_run;
      }
    },


    refreshInheritViewPayments(val) {
      if (val === true) {
        this.adminPermissions.media_buying_payments_affiliates_types_view
          = this.$_.cloneDeep(this.computedAcl.media_buying_payments_affiliates_types_view);
        this.adminPermissions.media_buying_payments_view_affiliates_visibility_teammates_id
          = this.$_.cloneDeep(this.computedAcl.media_buying_payments_view_affiliates_visibility_teammates_id);
      }
    },
    refreshInheritViewPaymentsModeration(val) {
      if (val === true) {
        this.adminPermissions.media_buying_payments_moderation_statuses_visible
          = this.computedAcl.media_buying_payments_moderation_statuses_visible;
      }
    },
    refreshInheritViewPaymentsPayout(val) {
      if (val === true) {
        this.adminPermissions.media_buying_payments_payout_statuses_visible
          = this.computedAcl.media_buying_payments_payout_statuses_visible;
      }
    },
    refreshInheritEditPayments(val) {
      if (val === true) {
        this.adminPermissions.media_buying_payments_affiliates_types_review
          = this.$_.cloneDeep(this.computedAcl.media_buying_payments_affiliates_types_review);
        this.adminPermissions.media_buying_payments_review_affiliates_visibility_teammates_id
          = this.$_.cloneDeep(this.computedAcl.media_buying_payments_review_affiliates_visibility_teammates_id);
      }
    },
    refreshInheritViewPosts(val) {
      if (val === true) {
        this.adminPermissions.media_buying_posts_affiliates_types_view
          = this.$_.cloneDeep(this.computedAcl.media_buying_posts_affiliates_types_view);
        this.adminPermissions.media_buying_posts_view_affiliates_visibility_teammates_id
          = this.$_.cloneDeep(this.computedAcl.media_buying_posts_view_affiliates_visibility_teammates_id);
      }
    },
    refreshInheritViewMediaReports(val) {
      if (val === true) {
        this.adminPermissions.media_buying_reports_affiliates_types_view
          = this.$_.cloneDeep(this.computedAcl.media_buying_reports_affiliates_types_view);
        this.adminPermissions.media_buying_reports_view_affiliates_visibility_teammates_id
          = this.$_.cloneDeep(this.computedAcl.media_buying_reports_view_affiliates_visibility_teammates_id);
      }
    },
    refreshInheritReviewPosts(val) {
      if (val === true) {
        this.adminPermissions.media_buying_posts_affiliates_types_review
          = this.$_.cloneDeep(this.computedAcl.media_buying_posts_affiliates_types_review);
        this.adminPermissions.media_buying_posts_review_affiliates_visibility_teammates_id
          = this.$_.cloneDeep(this.computedAcl.media_buying_posts_review_affiliates_visibility_teammates_id);
      }
    },
    refreshInheritPostStatuses(val) {
      if (val === true) {
        this.adminPermissions.media_buying_posts_statuses_visible =
          this.computedAcl.media_buying_posts_statuses_visible;
      }
    },

    refreshInheritPlayers(val) {
      if (val === true) {
        this.adminPermissions.players_available_property_groups = this.computedAcl.players_available_property_groups;
      }
    },

    refreshInheritGeo(val) {
      if (val === true) {
        const { countries_allowed_mode, countries_allowed_list } = this.adminPermissions;
        this.savedGeoValues = this.$_.cloneDeep({ countries_allowed_mode, countries_allowed_list });

        this.adminPermissions.countries_allowed_mode = this.computedAcl.countries_allowed_mode;
        this.adminPermissions.countries_allowed_list = this.computedAcl.countries_allowed_list;
      } else {
        const { countries_allowed_mode, countries_allowed_list } = this.savedGeoValues;

        if (countries_allowed_mode) this.adminPermissions.countries_allowed_mode = countries_allowed_mode;
        if (countries_allowed_list) this.adminPermissions.countries_allowed_list = countries_allowed_list;
      }
    },

    refreshInheritCatalog(val) {
      if (val === true) {
        this.adminPermissions.offers_link_to_catalog_categories_allowed_mode = this.computedAcl.offers_link_to_catalog_categories_allowed_mode;
        this.adminPermissions.offers_link_to_catalog_categories_allowed_list = this.$_.cloneDeep(this.computedAcl.offers_link_to_catalog_categories_allowed_list);
      }
    },

    close() {
      this.isLoading = true;
      this.errors = {};
      this.$emit('close');
    },

    checkWeight(currentOption, childOption) {
      if (!this.inherits[currentOption]) {
        const currentOptionVal = this.adminPermissions[currentOption];
        const childOptionVal = this.adminPermissions[childOption];
        if (childOptionVal && (this.crmWeight[childOptionVal] > this.crmWeight[currentOptionVal])) {
          this.adminPermissions[childOption] = this.adminPermissions[currentOption];
        }
      }
    },
    isDisabledOption(property, option) {
      const value = this.adminPermissions[property];
      return !this.crmAllowOptions[value].includes(option);
    },

    save() {
      this.isLoading = true;
      this.$_.forOwn(this.inherits, (value, key) => {
        if (key === 'countries_allowed_mode') {
          this.inherits.countries_allowed_list = this.inherits[key];
        }
        if (key === 'reports_affiliates_types') {
          this.inherits.reports_view_affiliates_visibility_teammates_id = this.inherits[key];
        }
        if (key === 'affiliates_types_view') {
          this.inherits.affiliates_view_affiliates_visibility_teammates_id = this.inherits[key];
        }
        if (key === 'affiliates_types_edit') {
          this.inherits.affiliates_edit_affiliates_visibility_teammates_id = this.inherits[key];
        }
        if (key === 'media_buying_payments_view') {
          this.inherits.media_buying_payments_view_affiliates_visibility_teammates_id = this.inherits[key];
        }
        if (key === 'media_buying_payments_review') {
          this.inherits.media_buying_payments_review_affiliates_visibility_teammates_id = this.inherits[key];
        }
        if (key === 'media_buying_posts_view') {
          this.inherits.media_buying_payments_view_affiliates_visibility_teammates_id = this.inherits[key];
        }
        if (key === 'media_buying_posts_review') {
          this.inherits.media_buying_posts_review_affiliates_visibility_teammates_id = this.inherits[key];
        }
        if (key === 'media_buying_reports_view') {
          this.inherits.media_buying_reports_view_affiliates_visibility_teammates_id = this.inherits[key];
        }
      });
      this.$_.forOwn(this.inherits, (value, key) => {
        if (value === true) {
          this.postPermissions[key] = null;
        } else {
          this.postPermissions[key] = this.adminPermissions[key];
        }
      });
      this.errors = {};
      this.$api.postAdminPermissions(this.userInfo.id, this.teamId, this.postPermissions)
        .then(() => {
          this.close();
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeep(error.data.errors);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    permissionIsDeny(permission) {
      return this.adminPermissions[permission] !== 'allow' && this.inherits[permission] === false;
    },

    inheritIsDeny(permission) {
      return this.inherits[permission];
    },

    updatePermissions(currentPermission, useSnapshot = true) {
      const currentPermissionsSettings = requiresForConfig[currentPermission];

      if (currentPermissionsSettings !== undefined && this.permissionIsDeny(currentPermission)) {
        if (useSnapshot) {
        // Get current state of permissions for switch off and save
          const permissionShapshot = getPermissionsSnapshot(this.adminPermissions, currentPermissionsSettings);
          this.permissionSnapshots[currentPermission] = permissionShapshot;

          // Get current state of inherits for switch off and save
          const inheritsShapshot = getInheritsSnapshot(this.inherits, currentPermissionsSettings);
          this.inheritsSnapshots[currentPermission] = inheritsShapshot;

          Object.assign(this.inherits, getInheritsObject(currentPermissionsSettings, false));
        }

        Object.assign(this.adminPermissions, getPermissionsObject(currentPermissionsSettings));

        const resetFields = resetFieldsConfig[currentPermission];
        if (resetFields) {
          Object.entries(resetFields).forEach(([key, value]) => {
            this.adminPermissions[key] = value;
          });
        }
      } else if (useSnapshot) {
        // Return saved permissions state
        const permissionShapshot = sortPermissionsSnapshot(
          this.permissionSnapshots[currentPermission],
          this.adminPermissions,
        );
        applyPermissionsSnapshot(this.adminPermissions, permissionShapshot);

        if (currentPermission === 'offers_edit') {
          this.adminPermissions.offers_link_to_catalog_categories_allowed_list = [];
          this.adminPermissions.offers_link_to_catalog_categories_allowed_mode = 'allow_all';
        }

        // Return saved inherits state
        const inheritsShapshot = sortInheritsSnapshot(this.inheritsSnapshots[currentPermission], this.inherits);
        Object.assign(this.inherits, {
          ...getInheritsObject(currentPermissionsSettings, false),
          ...inheritsShapshot,
        });
      }
    },

    updateInherits(currentPermission, useSnapshot = true) {
      const currentPermissionsSettings = requiresForConfig[currentPermission];

      if (currentPermissionsSettings !== undefined && this.inheritIsDeny(currentPermission)) {
        if (useSnapshot) {
        // Get current state of inherits for switch off and save
          const inheritsShapshot = getInheritsSnapshot(this.inherits, currentPermissionsSettings);
          this.inheritsSnapshots[currentPermission] = inheritsShapshot;
        }

        Object.assign(this.inherits, getInheritsObject(currentPermissionsSettings));
      } else if (useSnapshot) {
        // Return saved inherits state
        const inheritsShapshot = sortInheritsSnapshot(this.inheritsSnapshots[currentPermission], this.inherits);
        Object.assign(this.inherits, inheritsShapshot);
      }
    },

    getPermissionDepends(currentPermission) {
      return dependsOnConfig[currentPermission] ? dependsOnConfig[currentPermission] : [];
    },

    permissionIsDisabled(currentPermission) {
      const dependsOn = this.getPermissionDepends(currentPermission);

      return dependsOn.some((dependency) => this.permissionIsDeny(dependency));
    },

    inheritIsDisabled(currentPermission) {
      const dependsOn = this.getPermissionDepends(currentPermission);

      return dependsOn.some((dependency) => this.inheritIsDeny(dependency));
    },

    renderPermissionSwitchControl(permissionName) {
      const uiSwitch = !this.inherits[permissionName]
        ? h(resolveComponent('ui-switch'), {
          class: 't:px-16',
          disabled: this.permissionIsDisabled(permissionName) || this.inheritIsDisabled(permissionName),
          activeValue: 'allow',
          inactiveValue: 'deny',
          modelValue: this.adminPermissions[permissionName],
          onChange: (value) => {
            this.adminPermissions[permissionName] = value;
            this.updatePermissions(permissionName);
            this.updateInherits(permissionName, false);
          },
        })
        : h('div', {
          class: ['access-status', this.computedAcl[permissionName] === 'deny' ? 'access-status__deny' : ''],
        }, this.computedAcl[permissionName] ? this.$t(`permissions.${this.computedAcl[permissionName]}`) : '');

      const elCheckbox = h(resolveComponent('ui-checkbox'), {
        type: this.computedAcl[permissionName] === 'deny' ? 'negative' : '',
        disabled: this.inheritIsDisabled(permissionName) || this.permissionIsDisabled(permissionName),
        modelValue: this.inherits[permissionName],
        onChange: (value) => {
          this.inherits[permissionName] = value;
          this.updateInherits(permissionName);
          this.updatePermissions(permissionName, false);
          this.adminPermissions[permissionName] = this.computedAcl[permissionName];
        },
      });

      const inheritCheckWrapper = h('div', { class: 'inherit-check-wrapper' }, [elCheckbox]);

      const depends = this.getPermissionDepends(permissionName).map((dependency) => h('li', {
        class: {
          'dependency-item': true,
          selected: (this.adminPermissions[dependency] === 'allow' && this.inherits[dependency] === false),
        },
      }, this.$t(`permissions.list.${dependency}`)));

      const dependsOnText = h('h3', { class: 'depends-on-header' }, this.$t('permissions.depends'));
      const contentSlot = h('div', {}, [dependsOnText, depends]);

      const switchWrapper = h(
        'div',
        { class: 'values' },
        [inheritCheckWrapper, uiSwitch],
      );
      const elTooltip = depends.length
        ? h(resolveComponent('el-tooltip'),
          {
            placement: 'right',
            showAfter: 300,
          },
          {
            default: () => switchWrapper,
            content: () => contentSlot,
          },
        )
        : switchWrapper;

      const label = h(
        'span',
        { class: 'label' },
        this.$t(`permissions.list.${permissionName}`),
      );

      return h('div',
        { class: 'field' },
        [label, elTooltip],
      );
    },
  },
};
</script>
